/* global setTimeout */
/**
 * adminLinkNavigation.js
 *
 * The React-Native-touching half of the admin/services/app deep-linking
 * feature: wires `resolveAdminUrl` / `decideAdminNavigation` (the pure core
 * in `adminLinkResolver.js`) up to the real OS `Linking` API, the signed-in
 * user's management access, and the navigator.
 *
 * Kept in its own file — separate from `adminLinkResolver.js` — so the pure
 * mapping/decision logic can be Jest-tested without needing to mock
 * `react-native`, `@react-navigation/native`, AsyncStorage, or
 * `react-native-device-info` (all of which this file, transitively, touches).
 *
 * Cold start (`getInitialURL`) and warm start (`subscribe`) are BOTH wired
 * through `NavigationContainer`'s `linking` prop (see `AppProviders.jsx`) —
 * this file does not ALSO call `Linking.getInitialURL()` /
 * `Linking.addEventListener()` a second time itself. That would be exactly
 * the "double-handling" the task warns against. `helpers/widgets/widgetDeepLink.js`
 * keeps its own independent `Linking` subscription in `App.jsx` for
 * `happyapp://widget/...` links — that is a deliberately separate, untouched
 * code path (see `resolveAdminUrl`, which returns `null` for any non-https
 * / non-admin-host URL, including widget links, so this module never
 * competes with it).
 */

import {Linking, Platform} from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import {getUserInfo} from '../storage';
import {getManagementScope} from '../managementApi';
import {getSwitchableAppModesForUser} from '../managerRoles';
import {filterModesForAppVariant, isAppModeEnabled, getRouteForAppMode} from '../../config/appVariant';
import {getSetting} from '../persistentSettings';
import {applyHomeLanding} from '../homeLanding';
import {navigateWhenReady} from '../navigationRef';
import {resolveAdminUrl, decideAdminNavigation, getOwnWebOnlyUrl} from './adminLinkResolver';

// Mirrors helpers/widgets/widgetDeepLink.js: navigation may not be ready yet
// on a cold start (the URL tap is what launched the app), so retry briefly
// instead of dropping the navigation.
const MAX_ATTEMPTS = 20;
const RETRY_MS = 250;

const navigateWithRetry = (screen, params) => {
    if (!screen) return;
    let attempts = 0;
    const tryNavigate = () => {
        attempts += 1;
        const done = navigateWhenReady(screen, params);
        if (!done && attempts < MAX_ATTEMPTS) {
            setTimeout(tryNavigate, RETRY_MS);
        }
    };
    tryNavigate();
};

/**
 * Resolve + act on an incoming URL. Safe to call with ANY url — returns
 * `false` immediately for anything that isn't one of our three https hosts
 * (custom-scheme widget links included), so callers don't need to
 * pre-filter.
 * @param {string} url
 * @returns {Promise<boolean>} true when the URL belonged to this resolver (handled or intentionally ignored).
 */
export const handleAdminLinkUrl = async (url) => {
    const resolved = resolveAdminUrl(url);
    if (!resolved) {
        // Web-only page of ours (public guest meeting, …) that Android's
        // host-wide App Link handed to us: show it in an in-app browser tab
        // (a Custom Tab loads the URL directly — no bounce back into the app).
        const webOnlyUrl = Platform.OS !== 'web' ? getOwnWebOnlyUrl(url) : null;
        if (webOnlyUrl) {
            WebBrowser.openBrowserAsync(webOnlyUrl).catch(() => {});
            return true;
        }
        return false;
    }

    let managerAllowed = false;
    let salesModuleEnabled = true;
    try {
        salesModuleEnabled = isAppModeEnabled('restaurant');
        if (resolved.mode === 'manager') {
            const [user, scope] = await Promise.all([getUserInfo(), getManagementScope()]);
            managerAllowed = filterModesForAppVariant(getSwitchableAppModesForUser(user, scope?.place)).includes('manager');
        }
    } catch (_error) {
        // Fail closed (no access) rather than let a storage/role-lookup
        // error crash the app's cold open.
        managerAllowed = false;
    }

    const decision = decideAdminNavigation(resolved, {salesModuleEnabled, managerAllowed});

    if (decision.action === 'navigate' && decision.mode === 'any') {
        // Team Chat / meetings exist in every mode — open on top of whatever
        // mode the user is in, without switching (no navigator remount).
        navigateWithRetry(decision.screen, decision.params);
        return true;
    }

    if (decision.action === 'navigate') {
        // Persist `userAppMode` + let the navigator remount when the mode is
        // actually changing (existing homeLanding.js behavior — same
        // mechanism the in-app module switcher uses). No-ops (no remount)
        // when already in the right mode.
        await applyHomeLanding({mode: decision.mode === 'sales' ? 'restaurant' : decision.mode});
        navigateWithRetry(decision.screen, decision.params);
        return true;
    }

    if (decision.action === 'landing') {
        // No management access — don't touch/persist userAppMode, just make
        // sure the user is looking at THEIR OWN current mode's home instead
        // of wherever they happened to be, per "open app's main/landing page".
        const currentMode = (await getSetting('userAppMode', 'restaurant')) || 'restaurant';
        navigateWithRetry(getRouteForAppMode(currentMode));
        return true;
    }

    // action === 'none' — e.g. a services/app link opened on a build variant
    // with no Sales module bundled. Resolved, deliberately no navigation.
    return true;
};

/**
 * Custom `linking.getInitialURL` for `NavigationContainer`. Wraps the
 * standard `Linking.getInitialURL()` (the single call site for this
 * resolver — see file-level note) and fires the async handler for cold
 * starts, without blocking React Navigation's own cold-start URL capture.
 * @returns {Promise<string|null|undefined>}
 */
export const getInitialURL = async () => {
    const url = await Linking.getInitialURL();
    if (url) {
        handleAdminLinkUrl(url).catch(() => {});
    }
    return url ?? undefined;
};

/**
 * Custom `linking.subscribe` for `NavigationContainer`. Wraps the standard
 * `Linking.addEventListener('url', ...)` (again, the single call site for
 * this resolver) and fires the async handler for warm-start URLs. The
 * React Navigation `listener` is deliberately never invoked — see the inline
 * note for why forwarding it white-screens the web app.
 * @param {(url: string) => void} listener React Navigation's URL->state reconciler (intentionally unused).
 * @returns {() => void} unsubscribe
 */
export const subscribe = (listener) => {
    const onUrl = ({url}) => {
        if (url) handleAdminLinkUrl(url).catch(() => {});
        // Intentionally NOT forwarding to React Navigation's `listener`.
        // Routing here is 100% imperative (`handleAdminLinkUrl` above ->
        // `navigateWhenReady`), and `getStateFromPath` is a deliberate no-op that
        // returns `undefined` (see `adminLinkResolver.js`). On web, invoking
        // `listener(url)` makes React Navigation's linking reconciler call
        // `getStateFromPath(url)` -> `undefined` and then, on the subscribe/popstate
        // path, `resetRoot(state)` WITHOUT the `if (state)` guard its cold-start
        // path has. `resetRoot(undefined)` -> `CommonActions.reset(undefined)`, whose
        // RESET reducer reads `.routes` of `undefined` and throws an UNCAUGHT
        // "Cannot read properties of undefined (reading 'routes')" that white-screens
        // the web app (observed on DayOpeningScreen, appVersion 4.1.1). Since RN must
        // never drive navigation from a URL in this app, we skip the reconciler
        // entirely. `getInitialURL` (cold start) is unaffected — that path is guarded.
        void listener;
    };
    const sub = Linking.addEventListener('url', onUrl);
    return () => sub.remove();
};

export default handleAdminLinkUrl;
